import { toast } from '@heroui/react';
import { useCallback, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import {
  Button,
  Description,
  FieldError,
  Input,
  Label,
  ListBox,
  Modal,
  Select,
  TextField,
} from '@heroui/react';
import { CellSwitch } from '@heroui-pro/react';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import { AI_MODEL_TYPES, AI_MODEL_TYPE_LABELS } from '../constants';

const modelSchema = z.object({
  providerId: z.string().min(1, '请选择供应商'),
  modelCode: z.string().min(1, '模型编码不能为空').max(100, '模型编码长度不能超过100字符'),
  modelVersion: z.string().max(50, '模型版本长度不能超过50字符').optional(),
  modelName: z.string().min(1, '显示名称不能为空').max(100, '显示名称长度不能超过100字符'),
  modelType: z.enum(AI_MODEL_TYPES),
  contextLength: z
    .union([
      z.literal(''),
      z.coerce.number().int('上下文长度须为整数').min(0, '上下文长度不能为负数'),
    ])
    .optional(),
  maxOutputTokens: z
    .union([
      z.literal(''),
      z.coerce.number().int('最大输出 Token 须为整数').min(0, '最大输出 Token 不能为负数'),
    ])
    .optional(),
  isMultimodal: z.boolean().default(false),
  isEnabled: z.boolean().default(true),
});

type ModelFormValues = z.infer<typeof modelSchema>;

export type AiModelFormItem = {
  id?: string;
  providerId?: string | null;
  modelCode?: string | null;
  modelVersion?: string | null;
  modelName?: string | null;
  modelType?: string | null;
  contextLength?: number | null;
  maxOutputTokens?: number | null;
  isMultimodal?: boolean | null;
  isEnabled?: boolean | null;
};

interface AiModelEditorModalProps {
  open: boolean;
  model?: AiModelFormItem | null;
  onCancel: () => void;
  onSuccess: () => void;
}

function parseOptionalInt(value: string | number | undefined): number | null {
  if (value === '' || value === undefined || value === null) return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

export function AiModelEditorModal({ open, model, onCancel, onSuccess }: AiModelEditorModalProps) {
  const utils = trpc.useUtils();
  const isEdit = !!model;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);

  const { data: providersData } = trpc.aiProvider.listAll.useQuery(
    {},
    { enabled: isHydrated && isAuthenticated && open }
  );

  const providers = (providersData ?? []) as Array<{
    id: string;
    name?: string | null;
    code?: string | null;
  }>;

  const handleModalOpenChange = useCallback((next: boolean) => {
    if (!next) onCancelRef.current();
  }, []);

  const form = useForm<ModelFormValues>({
    resolver: zodResolver(modelSchema) as any,
    defaultValues: {
      providerId: '',
      modelCode: '',
      modelVersion: '',
      modelName: '',
      modelType: 'CHAT',
      contextLength: '',
      maxOutputTokens: '',
      isMultimodal: false,
      isEnabled: true,
    },
    mode: 'onChange',
  });
  const { reset } = form;

  const createModelMutation = trpc.aiModel.create.useMutation({
    onSuccess: () => {
      toast.success('AI 模型创建成功');
      form.reset();
      onSuccess();
      void utils.aiModel.list.invalidate();
    },
  });

  const updateModelMutation = trpc.aiModel.update.useMutation({
    onSuccess: () => {
      toast.success('AI 模型更新成功');
      form.reset();
      onSuccess();
      void utils.aiModel.list.invalidate();
    },
  });

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    if (isEdit && model) {
      reset({
        providerId: model.providerId || '',
        modelCode: model.modelCode || '',
        modelVersion: model.modelVersion || '',
        modelName: model.modelName || '',
        modelType: (AI_MODEL_TYPES as readonly string[]).includes(model.modelType ?? '')
          ? (model.modelType as (typeof AI_MODEL_TYPES)[number])
          : 'CHAT',
        contextLength: model.contextLength != null ? model.contextLength : '',
        maxOutputTokens: model.maxOutputTokens != null ? model.maxOutputTokens : '',
        isMultimodal: model.isMultimodal ?? false,
        isEnabled: model.isEnabled ?? true,
      });
    } else {
      reset({
        providerId: providers[0]?.id ?? '',
        modelCode: '',
        modelVersion: '',
        modelName: '',
        modelType: 'CHAT',
        contextLength: '',
        maxOutputTokens: '',
        isMultimodal: false,
        isEnabled: true,
      });
    }
  }, [open, isEdit, model, reset, providers]);

  const onSubmit = async (values: ModelFormValues) => {
    const contextLength = parseOptionalInt(values.contextLength);
    const maxOutputTokens = parseOptionalInt(values.maxOutputTokens);
    const modelVersion = values.modelVersion?.trim() ?? '';

    if (isEdit && model?.id) {
      await updateModelMutation.mutateAsync({
        id: model.id,
        data: {
          providerId: values.providerId,
          modelCode: values.modelCode,
          modelVersion,
          modelName: values.modelName,
          modelType: values.modelType,
          contextLength,
          maxOutputTokens,
          isMultimodal: values.isMultimodal,
          isEnabled: values.isEnabled,
        },
      });
    } else {
      await createModelMutation.mutateAsync({
        providerId: values.providerId,
        modelCode: values.modelCode,
        modelVersion,
        modelName: values.modelName,
        modelType: values.modelType,
        contextLength,
        maxOutputTokens,
        isMultimodal: values.isMultimodal,
        isEnabled: values.isEnabled,
      });
    }
  };

  const isLoading = createModelMutation.isPending || updateModelMutation.isPending;
  const errors = form.formState.errors;

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="lg">
          <Modal.Dialog
            key={open ? (isEdit && model ? `edit-${String(model.id ?? '')}` : 'create') : 'closed'}
          >
            <Modal.CloseTrigger />
            <Modal.Header className="gap-1">
              <Modal.Heading className="text-xl font-medium">
                {isEdit ? '编辑 AI 模型' : '创建 AI 模型'}
              </Modal.Heading>
              <Description className="text-sm">
                {isEdit ? '修改模型配置' : '填写模型信息以创建新模型'}
              </Description>
            </Modal.Header>
            <Modal.Body className="gap-4 p-2">
              <form
                id="editor-ai-model-form"
                onSubmit={form.handleSubmit(onSubmit as any)}
                className="flex min-w-0 flex-col gap-4"
              >
                <Controller
                  control={form.control}
                  name="providerId"
                  render={({ field }) => (
                    <Select
                      className="w-full"
                      isInvalid={!!errors.providerId}
                      variant="secondary"
                      value={field.value || null}
                      onChange={(key) => {
                        if (key != null) field.onChange(String(key));
                      }}
                    >
                      <Label className="text-sm font-medium">
                        供应商 <span className="text-danger">*</span>
                      </Label>
                      <Select.Trigger>
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <Select.Popover>
                        <ListBox>
                          {providers.map((p) => (
                            <ListBox.Item
                              key={p.id}
                              id={p.id}
                              textValue={p.name || p.code || p.id}
                            >
                              {p.name || p.code || p.id}
                            </ListBox.Item>
                          ))}
                        </ListBox>
                      </Select.Popover>
                      {errors.providerId ? (
                        <FieldError>{errors.providerId.message}</FieldError>
                      ) : null}
                    </Select>
                  )}
                />

                <Controller
                  control={form.control}
                  name="modelCode"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.modelCode}>
                      <Label className="text-sm font-medium">
                        模型编码 <span className="text-danger">*</span>
                      </Label>
                      <Input
                        maxLength={100}
                        placeholder="与 common/ai 第三层目录一致"
                        variant="secondary"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.modelCode ? <FieldError>{errors.modelCode.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="modelVersion"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.modelVersion}>
                      <Label className="text-sm font-medium">模型版本</Label>
                      <Input
                        maxLength={50}
                        placeholder="可选，与 model_code 联合唯一"
                        variant="secondary"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.modelVersion ? (
                        <FieldError>{errors.modelVersion.message}</FieldError>
                      ) : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="modelName"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.modelName}>
                      <Label className="text-sm font-medium">
                        显示名称 <span className="text-danger">*</span>
                      </Label>
                      <Input
                        maxLength={100}
                        placeholder="请输入显示名称"
                        variant="secondary"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.modelName ? <FieldError>{errors.modelName.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="modelType"
                  render={({ field }) => (
                    <Select
                      className="w-full"
                      isInvalid={!!errors.modelType}
                      variant="secondary"
                      value={field.value}
                      onChange={(key) => {
                        if (key != null) field.onChange(String(key));
                      }}
                    >
                      <Label className="text-sm font-medium">
                        模型类型 <span className="text-danger">*</span>
                      </Label>
                      <Select.Trigger>
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <Select.Popover>
                        <ListBox>
                          {AI_MODEL_TYPES.map((t) => (
                            <ListBox.Item key={t} id={t} textValue={AI_MODEL_TYPE_LABELS[t]}>
                              {AI_MODEL_TYPE_LABELS[t]}
                            </ListBox.Item>
                          ))}
                        </ListBox>
                      </Select.Popover>
                      {errors.modelType ? <FieldError>{errors.modelType.message}</FieldError> : null}
                    </Select>
                  )}
                />

                <Controller
                  control={form.control}
                  name="contextLength"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.contextLength}>
                      <Label className="text-sm font-medium">上下文长度</Label>
                      <Input
                        type="number"
                        min={0}
                        placeholder="Token 类模型可选"
                        variant="secondary"
                        value={field.value === '' ? '' : String(field.value ?? '')}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.contextLength ? (
                        <FieldError>{errors.contextLength.message}</FieldError>
                      ) : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="maxOutputTokens"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.maxOutputTokens}>
                      <Label className="text-sm font-medium">最大输出 Token</Label>
                      <Input
                        type="number"
                        min={0}
                        placeholder="可选"
                        variant="secondary"
                        value={field.value === '' ? '' : String(field.value ?? '')}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.maxOutputTokens ? (
                        <FieldError>{errors.maxOutputTokens.message}</FieldError>
                      ) : null}
                    </TextField>
                  )}
                />

                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium">多模态</Label>
                  <Controller
                    control={form.control}
                    name="isMultimodal"
                    render={({ field }) => (
                      <CellSwitch
                        aria-label="多模态"
                        variant="secondary"
                        isSelected={!!field.value}
                        onChange={field.onChange}
                      >
                        <CellSwitch.Trigger>
                          <CellSwitch.Label>{field.value ? '是' : '否'}</CellSwitch.Label>
                          <CellSwitch.Control />
                        </CellSwitch.Trigger>
                      </CellSwitch>
                    )}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium">是否启用</Label>
                  <Controller
                    control={form.control}
                    name="isEnabled"
                    render={({ field }) => (
                      <CellSwitch
                        aria-label="是否启用"
                        variant="secondary"
                        isSelected={!!field.value}
                        onChange={field.onChange}
                      >
                        <CellSwitch.Trigger>
                          <CellSwitch.Label>
                            {field.value ? '已启用' : '已停用'}
                          </CellSwitch.Label>
                          <CellSwitch.Control />
                        </CellSwitch.Trigger>
                      </CellSwitch>
                    )}
                  />
                </div>
              </form>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="ghost" onPress={onCancel}>
                取消
              </Button>
              <Button
                isDisabled={isLoading}
                variant="primary"
                onPress={() => void form.handleSubmit(onSubmit as any)()}
              >
                {isLoading ? '提交中…' : isEdit ? '更新' : '创建'}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
